/**
 * useMachine — Fetch a single machine by database ID with auto-refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { getMachine, type Machine } from '../api/client';

const REFRESH_INTERVAL_MS = 30_000;

interface UseMachineResult {
  machine: Machine | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useMachine(id: number): UseMachineResult {
  const [machine, setMachine] = useState<Machine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getMachine(id);
      setMachine(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch machine');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setMachine(null);
    setError(null);
    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  return { machine, loading, error, refresh };
}
